import React from "react";

import { Button, FormControl, FormLabel, Typography } from "@portex-pro/ui-components";
import { AddressType } from "api/types/generated-types";
import LocationPicker from "components/addresses/LocationPicker";
import { Guide } from "components/Guide";
import SingleCalendarPicker from "components/SingleCalendarPicker";
import { DateTime } from "luxon";
import FormSelectView from "pages/shipper/pages/request-quote/components/FormSelectView";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IncotermsEnum } from "types/Incoterms";
import { RoutingType, RoutingTypeEnum } from "types/RoutingType";

import RoutingTabPanel from "../../_components/RoutingTabPanel";
import StepCollapse from "../../_components/StepCollapse";
import UnloadingDetailsSelect from "../../_components/UnloadingDetailsSelect";
import { useCreateStepCollapseProps } from "../../_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "../../_store";
import { quoteRequestActions } from "../../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../../types";
import { FinalSchema, StopSchema } from "../_schemas";

interface RoutingStepTagProps {
  originAddress?: {
    name?: string | null;
    city?: string | null;
    country_name?: string | null;
    port_name?: string | null;
  };
  destinationAddress?: {
    name?: string | null;
    city?: string | null;
    country_name?: string | null;
    port_name?: string | null;
  };
  cargoReadyDate?: string | null;
  routingType?: string;
}

const RoutingStepTag: React.FC<RoutingStepTagProps> = ({
  originAddress,
  destinationAddress,
  cargoReadyDate,
  routingType,
}) => {
  if (!originAddress || !destinationAddress || !routingType) {
    return <></>;
  }

  let originName = "";
  let destinationName = "";

  if (routingType === "P2D") {
    // Port to Door: origin is port, destination is country
    originName = originAddress.port_name || "";
    destinationName = destinationAddress.country_name || originAddress.city || "";
  } else if (routingType === "D2P") {
    // Door to Port: origin is country, destination is port
    originName = originAddress.country_name || originAddress.city || "";
    destinationName = destinationAddress.port_name || "";
  }

  if (!originName || !destinationName) {
    return <></>;
  }

  const dateDisplay = cargoReadyDate ? DateTime.fromISO(cargoReadyDate).toFormat("MMM dd") : "";

  return (
    <div className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1">
      <span className="text-blue-600 font-bold text-sm">
        {originName} – {destinationName}
      </span>
      {dateDisplay && <span className="text-blue-600 font-normal text-sm ml-1">{dateDisplay}</span>}
    </div>
  );
};

function mapToAddressType(type: string | null | undefined): AddressType | undefined {
  if (!type) return undefined;
  if (Object.values(AddressType).includes(type as AddressType)) {
    return type as AddressType;
  }
  return undefined;
}

export const RoutingStep: React.FC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["common", "shipper"]);
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const { stepIndex, quoteRequest: airQuoteRequest } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const { baseStepCollapseProps, setIsOpen } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Routing,
    name: t("common:routing"),
    number: 1,
  });

  const methods = useFormContext<FinalSchema>();

  const routing_type = useWatch({ control: methods.control, name: "routing_type" });
  const stops = useWatch({ control: methods.control, name: "stops" });
  const cargoReadyDate = useWatch({ control: methods.control, name: "cargo_ready_date" });

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Shipment);
    setIsOpen(false);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      tag={
        <RoutingStepTag
          originAddress={stops?.[0]?.address}
          destinationAddress={stops?.[1]?.address}
          cargoReadyDate={cargoReadyDate}
          routingType={routing_type}
        />
      }
      footer={
        stepIndex <= QuoteStepIndex.Routing ? (
          <div className="flex justify-between mt-2 w-full">
            <div />
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={methods.handleSubmit(submit)}
              style={{ width: 150 }}
            >
              {t("common:next")}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="grid grid-cols-4 gap-x-4 gap-y-6 my-6">
        <Controller
          control={methods.control}
          name="incoterms"
          render={({ field, fieldState }) => (
            <Guide className="w-full flex flex-col" showGuide={!field.value && !fieldState.error}>
              <FormSelectView
                value={field.value}
                items={Object.values(IncotermsEnum)}
                getItemCopy={(incoterm) => t(`common:incoterms_map.${incoterm}`)}
                onChange={(value) => setQuoteRequestAction({ incoterms: value })}
                formControlProps={{ required: true }}
                highlight={!!methods.formState.errors.incoterms}
                label={t("common:incoterms")}
              />
              {methods.formState.errors.incoterms && (
                <Typography color="error" variant="caption">
                  {methods.formState.errors.incoterms.message}
                </Typography>
              )}
            </Guide>
          )}
        />

        {/* Routing Type Section */}
        <div>
          <Controller
            control={methods.control}
            name="routing_type"
            render={({ field, fieldState }) => (
              <FormControl fullWidth>
                <FormLabel required>{t("shipper:routingStep.selectRouting")}</FormLabel>
                <Guide showGuide={!field.value && !fieldState.error}>
                  <RoutingTabPanel
                    className="guideGroup"
                    selected={field.value as unknown as RoutingType}
                    onSelect={(routing) => {
                      setQuoteRequestAction({
                        routing_type: routing as unknown as RoutingTypeEnum,
                      });
                    }}
                    hasError={!!methods.formState.errors.routing_type}
                    routingType="PORTS_AND_DOORS"
                  />
                </Guide>
                {fieldState.error && (
                  <Typography color="error" variant="caption">
                    {fieldState.error.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </div>

        {/* Unloading Details Section */}
        <div className="col-span-2">
          <Controller
            control={methods.control}
            name="unloading_details"
            render={({ field, fieldState }) => (
              <FormControl fullWidth>
                <FormLabel required>{t("common:unloadingDetails")}</FormLabel>
                <Guide showGuide={!field.value && !fieldState.error}>
                  <UnloadingDetailsSelect
                    className="guideGroup"
                    selected={field.value}
                    onSelect={(unloadingDetais) => setQuoteRequestAction({ unloading_details: unloadingDetais })}
                    hasError={!!methods.formState.errors.unloading_details}
                  />
                </Guide>
                {fieldState.error && (
                  <Typography color="error" variant="caption">
                    {fieldState.error.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </div>

        {routing_type !== undefined && (
          <>
            {/* Locations Section */}
            {/* Origin */}
            <div>
              <Controller
                control={methods.control}
                name="stops.0"
                render={({ field, fieldState }) => (
                  <FormControl fullWidth>
                    <FormLabel required>
                      {routing_type === "P2D" ? t("shipper:drayage.portOfOrigin") : t("shipper:pickup")}
                    </FormLabel>
                    <Guide showGuide={!field.value.address && !fieldState.error}>
                      <LocationPicker
                        // @ts-expect-error this is fine for now
                        value={field.value.address}
                        onChange={(address) =>
                          setQuoteRequestAction({
                            stops: [
                              {
                                ...airQuoteRequest.stops?.[0],
                                address: {
                                  ...((address as unknown as StopSchema) ?? []),
                                  id: Number(address?.id),
                                  type: mapToAddressType(address?.type),
                                },
                              },
                              airQuoteRequest.stops?.[1] ?? {},
                            ],
                          })
                        }
                        mode="DRAYAGE"
                        toDoor={routing_type === "D2P"}
                        placeholder={
                          routing_type === "P2D" ? t("shipper:drayage.selectPort") : t("shipper:drayage.selectLocation")
                        }
                        startIconPallete="grey"
                      />
                    </Guide>
                    {methods.formState.errors.stops?.[0]?.address?.message && (
                      <Typography color="error" variant="caption">
                        {methods.formState.errors.stops?.[0]?.address?.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </div>

            {/* Destination */}
            <div className="">
              <Controller
                control={methods.control}
                name="stops.1"
                render={({ field, fieldState }) => (
                  <FormControl fullWidth>
                    <FormLabel required>
                      {routing_type === "D2P" ? t("shipper:drayage.portOfDestination") : t("shipper:delivery")}
                    </FormLabel>
                    <Guide showGuide={!field.value.address && !fieldState.error}>
                      <LocationPicker
                        // @ts-expect-error this is fine for now
                        value={field.value.address}
                        onChange={(address) =>
                          setQuoteRequestAction({
                            stops: [
                              airQuoteRequest.stops?.[0] ?? {},
                              {
                                ...airQuoteRequest.stops?.[1],
                                address: {
                                  ...((address as unknown as StopSchema) ?? []),
                                  id: Number(address?.id),
                                  type: mapToAddressType(address?.type),
                                },
                              },
                            ],
                          })
                        }
                        mode="DRAYAGE"
                        toDoor={routing_type === "P2D"}
                        placeholder={
                          routing_type === "D2P" ? t("shipper:drayage.selectPort") : t("shipper:drayage.selectLocation")
                        }
                        startIconPallete="grey"
                      />
                    </Guide>
                    {methods.formState.errors.stops?.[1]?.address?.message && (
                      <Typography color="error" variant="caption">
                        {methods.formState.errors.stops?.[1]?.address?.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </div>

            {/* Pickup Date & Time Section */}
            <div className="">
              <Controller
                control={methods.control}
                name="cargo_ready_date"
                render={({ field, fieldState }) => (
                  <FormControl fullWidth>
                    <FormLabel required>{t("shipper:cargoReadyDate")}</FormLabel>
                    <Guide showGuide={!field.value && !fieldState.error}>
                      <SingleCalendarPicker
                        startIconPallete="grey"
                        value={field.value ? DateTime.fromISO(field.value) : null}
                        onChange={(cargoReadyDate) =>
                          setQuoteRequestAction({
                            stops: [
                              {
                                ...airQuoteRequest.stops?.[0],
                                start: cargoReadyDate
                                  ? cargoReadyDate
                                      .setZone(airQuoteRequest.stops?.[0]?.address?.iana_timezone ?? "UTC", {
                                        keepLocalTime: true,
                                      })
                                      .toISO()
                                  : null,
                              },
                              airQuoteRequest.stops?.[1] ?? {},
                            ],
                          })
                        }
                      />
                    </Guide>
                    {fieldState.error && (
                      <Typography color="error" variant="caption">
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </div>

            <div className="">
              <Controller
                control={methods.control}
                name="target_delivery_date"
                render={({ field }) => (
                  <FormControl fullWidth>
                    <FormLabel>{t("shipper:targetDestinationDeliveryDate")}</FormLabel>
                    <SingleCalendarPicker
                      required={!!methods.formState.errors.target_delivery_date}
                      startIconPallete="grey"
                      value={field.value ? DateTime.fromISO(field.value) : null}
                      minDate={cargoReadyDate ? DateTime.fromISO(cargoReadyDate, { setZone: true }) : undefined}
                      onChange={(targetDeliveryDate) =>
                        setQuoteRequestAction({
                          stops: [
                            airQuoteRequest.stops?.[0] ?? {},
                            {
                              ...airQuoteRequest.stops?.[1],
                              start: targetDeliveryDate
                                ? targetDeliveryDate
                                    .setZone(airQuoteRequest.stops?.[1]?.address?.iana_timezone ?? "UTC", {
                                      keepLocalTime: true,
                                    })
                                    .toISO()
                                : null,
                            },
                          ],
                        })
                      }
                    />
                  </FormControl>
                )}
              />
            </div>
          </>
        )}
      </div>
    </StepCollapse>
  );
};

export default RoutingStep;
